import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';
import { FeatureFlags, type SelectedKeysMap, type TreeNode } from '@centric-os/types';
import { useEventListener } from '@vueuse/core';
import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default (treeNodesToTrack: Ref<TreeNode[]>, selectedKeys: Ref<SelectedKeysMap>) => {
  const { t } = useI18n();
  const { isFeatureOn } = useSplitIoStore();

  const treeNodes = ref(treeNodesToTrack.value ? [...treeNodesToTrack.value] : []);

  let shiftIsHeld = false;
  let lastShiftClickedNode: TreeNode | null;
  let anchorNode: TreeNode | null;
  let lastUpdates: SelectedKeysMap = {};

  const handleMultiSelect = async (node: TreeNode) => {
    await nextTick();

    if (!shiftIsHeld) {
      // Regular click, reset shift tracking & set new anchor index
      lastShiftClickedNode = null;
      anchorNode = node;
      lastUpdates = {};
      return;
    }
    if (!anchorNode) {
      // If no previous node, select from the starting of tree to selected node
      const rootKey = treeNodes.value[0].key;
      const nodes = getNodesForMultiSelection(treeNodes.value, rootKey, node.key);

      Object.entries(nodes).forEach(([key, value]) => {
        selectedKeys.value[key] = value;
      });
      lastUpdates = nodes;
      // Set that as the new anchor node
      anchorNode = node;
    } else {
      const nodes = getNodesForMultiSelection(treeNodes.value, anchorNode.key, node.key);
      const updatedSelectedKeys = JSON.parse(JSON.stringify(selectedKeys.value));
      // Unselect previously selected nodes if they're not in the current selection
      Object.keys(lastUpdates).forEach((key) => {
        if (!nodes[key]) {
          updatedSelectedKeys[key] = { checked: false, partialChecked: false };
        }
      });
      // Apply current selection updates
      Object.entries(nodes).forEach(([key, value]) => {
        updatedSelectedKeys[key] = value;
      });
      selectedKeys.value = updatedSelectedKeys;
      lastUpdates = nodes;
    }
    lastShiftClickedNode = node;
  };

  const flattenTree = (nodes: TreeNode[]) => {
    const flatList: TreeNode[] = [];
    const processNode = (node: TreeNode) => {
      flatList.push(node);
      if (node.children) {
        node.children.forEach((child) => processNode(child));
      }
    };
    nodes.forEach((node) => processNode(node));
    return flatList;
  };
  const getNodesForMultiSelection = (
    treeNodes: TreeNode[],
    key1: String,
    key2: String,
  ): SelectedKeysMap => {
    const flatNodes = flattenTree(treeNodes);
    const index1 = flatNodes.findIndex((node) => node.key === key1);
    const index2 = flatNodes.findIndex((node) => node.key === key2);
    const results: SelectedKeysMap = {};

    if (index1 === -1 || index2 === -1) {
      return results;
    }

    // Order the indices to slice correctly
    let start = Math.min(index1, index2);
    const end = Math.max(index1, index2);
    const selectedNodes = flatNodes.slice(start, end + 1);

    const addParentNode = (node: TreeNode) => {
      // Recurse to add all ancestors
      if (node.parentKey) {
        const parent = flatNodes.find((n) => n.key === node.parentKey);
        if (parent) {
          selectedNodes.unshift(parent);
          addParentNode(parent);
        }
      }
    };

    //Grab all parents from the first selected node.
    const parent = flatNodes.find((node) => node.key === selectedNodes[0].parentKey);
    if (parent) {
      selectedNodes.unshift(parent);
      addParentNode(parent);
    }

    // Grab all children from the last selected node
    if (selectedNodes[selectedNodes.length - 1].children?.length) {
      const lastElementChildren = flattenTree([selectedNodes[selectedNodes.length - 1]]);
      selectedNodes.push(...lastElementChildren);
    }

    // Remove dupes
    const keysToBeSelected = new Set(selectedNodes.map((node) => node.key));

    selectedNodes.forEach((node) => {
      // Flatten out nodes children to get checked/partialChecked status for ancestor
      const nodeDescendents = flattenTree(flatNodes.filter((n) => n.parentKey === node.key));
      const allDescendentsSelected = nodeDescendents.every((descendent) =>
        keysToBeSelected.has(descendent.key),
      );
      const someDescendentsSelected = nodeDescendents.some((descendent) =>
        keysToBeSelected.has(descendent.key),
      );

      results[node.key] = {
        checked: nodeDescendents.length ? allDescendentsSelected : true,
        partialChecked: nodeDescendents.length
          ? someDescendentsSelected && !allDescendentsSelected
          : false,
      };
    });

    return results;
  };

  // Tree table's @node-select event doesn't provide an event object.
  // Use this to track shift
  useEventListener(document, 'keydown', (evt) => {
    if (evt.key === 'Shift') {
      shiftIsHeld = true;
    }
  });
  useEventListener(document, 'keyup', (evt) => {
    if (evt.key === 'Shift') {
      shiftIsHeld = false;
    }
  });

  // Watch for changes in the array(Sorting the table)
  watch(
    treeNodesToTrack,
    (newValue) => {
      if (!newValue?.length) return;
      treeNodes.value = [...newValue];
      anchorNode = null;
      lastShiftClickedNode = null;
    },
    {
      deep: true,
    },
  );

  return {
    handleMultiSelect,
    flattenTree,
  };
};
